/**
 * Un petit écrivain de PDF, sans dépendance.
 *
 * Le compte rendu d'un événement est du texte et des tableaux : les polices
 * standard du PDF (Helvetica, Helvetica-Bold) en codage WinAnsi couvrent le
 * français — accents, « guillemets », œ, €. Pas besoin d'une bibliothèque
 * de 300 Ko pour ça, ni d'une nouvelle dépendance à installer sur le PC.
 *
 * Mesure du texte : les largeurs de Helvetica (unités de 1/1000 em) pour
 * l'ASCII ; une lettre accentuée prend la largeur de sa lettre de base.
 */

const L_ASCII: Record<string, number> = {};
(() => {
  const w = [
    278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722,
    667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222,
    500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
  ];
  for (let c = 32; c <= 126; c++) L_ASCII[String.fromCharCode(c)] = w[c - 32]!;
})();

const SPECIAUX: Record<string, number> = {
  "€": 0x80,
  "…": 0x85,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97,
  "œ": 0x9c,
  "Œ": 0x8c,
  " ": 0xa0,
  " ": 0x20,
};

function octet(ch: string): number {
  if (SPECIAUX[ch] !== undefined) return SPECIAUX[ch]!;
  const c = ch.charCodeAt(0);
  if (c < 256 && !(c >= 0x80 && c < 0xa0)) return c;
  return 0x3f; // « ? » pour ce que WinAnsi n'a pas
}

function largeurCar(ch: string): number {
  const base = ch.normalize("NFD")[0] ?? ch;
  return L_ASCII[base] ?? (ch === "œ" ? 944 : ch === "—" ? 1000 : ch === "…" ? 1000 : 556);
}

export function largeur(t: string, taille: number, gras = false): number {
  let s = 0;
  for (const ch of t) s += largeurCar(ch);
  return (s / 1000) * taille * (gras ? 1.06 : 1);
}

/** Une chaîne PDF littérale, en octets WinAnsi. */
function litteral(t: string): string {
  let out = "(";
  for (const ch of t) {
    const b = octet(ch);
    const c = String.fromCharCode(b);
    out += c === "(" || c === ")" || c === "\\" ? `\\${c}` : c;
  }
  return `${out})`;
}

type Rgb = [number, number, number];
const NOIR: Rgb = [0.07, 0.07, 0.07];
const GRIS: Rgb = [0.4, 0.4, 0.4];

export class Pdf {
  private pages: string[] = [];
  private flux = "";
  private y = 0;
  readonly l = 595.28;
  readonly h = 841.89;
  readonly marge = 48;

  constructor(private readonly pied: string) {
    this.nouvellePage();
  }

  private nouvellePage(): void {
    if (this.flux) this.pages.push(this.flux);
    this.flux = "";
    this.y = this.h - this.marge;
  }

  private place(besoin: number): void {
    if (this.y - besoin < this.marge + 20) this.nouvellePage();
  }

  get largeurUtile(): number {
    return this.l - 2 * this.marge;
  }

  /** Couper un texte à la largeur donnée. */
  couper(t: string, taille: number, max: number, gras = false): string[] {
    const lignes: string[] = [];
    for (const para of t.split("\n")) {
      let courante = "";
      for (const mot of para.split(/\s+/)) {
        if (!mot) continue;
        const essai = courante ? `${courante} ${mot}` : mot;
        if (largeur(essai, taille, gras) <= max) courante = essai;
        else {
          if (courante) lignes.push(courante);
          // Un mot plus long que la ligne : coupé net.
          let m = mot;
          while (largeur(m, taille, gras) > max && m.length > 1) {
            let n = m.length;
            while (n > 1 && largeur(m.slice(0, n), taille, gras) > max) n--;
            lignes.push(m.slice(0, n));
            m = m.slice(n);
          }
          courante = m;
        }
      }
      lignes.push(courante);
    }
    return lignes;
  }

  private ecrire(x: number, y: number, t: string, taille: number, gras: boolean, c: Rgb): void {
    this.flux += `BT ${c[0]} ${c[1]} ${c[2]} rg /${gras ? "F2" : "F1"} ${taille} Tf 1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm ${litteral(t)} Tj ET\n`;
  }

  texte(t: string, o: { taille?: number; gras?: boolean; gris?: boolean; apres?: number; retrait?: number } = {}): void {
    const taille = o.taille ?? 10.5;
    const gras = o.gras ?? false;
    const interligne = taille * 1.35;
    const x = this.marge + (o.retrait ?? 0);
    for (const ligne of this.couper(t, taille, this.largeurUtile - (o.retrait ?? 0), gras)) {
      this.place(interligne);
      this.y -= interligne;
      this.ecrire(x, this.y + taille * 0.3, ligne, taille, gras, o.gris ? GRIS : NOIR);
    }
    this.y -= o.apres ?? 4;
  }

  titre(t: string): void {
    this.texte(t, { taille: 22, gras: true, apres: 2 });
  }

  section(t: string): void {
    this.place(40);
    this.y -= 12;
    this.texte(t, { taille: 14, gras: true, apres: 2 });
    this.trait();
  }

  trait(): void {
    this.place(6);
    this.flux += `0.8 0.8 0.8 RG 0.8 w ${this.marge} ${this.y.toFixed(2)} m ${(this.l - this.marge).toFixed(2)} ${this.y.toFixed(2)} l S\n`;
    this.y -= 6;
  }

  espace(n: number): void {
    this.y -= n;
  }

  /** Des chiffres côte à côte : un nombre en gros, son libellé dessous. */
  chiffres(items: Array<[string, string]>): void {
    const n = items.length;
    const w = this.largeurUtile / n;
    this.place(46);
    const y0 = this.y;
    items.forEach(([v, lib], i) => {
      const x = this.marge + i * w;
      this.ecrire(x, y0 - 22, v, 20, true, NOIR);
      const lignes = this.couper(lib, 8.5, w - 8);
      lignes.slice(0, 2).forEach((l, k) => this.ecrire(x, y0 - 34 - k * 10, l, 8.5, false, GRIS));
    });
    this.y = y0 - 58;
  }

  /** Un tableau : colonnes proportionnelles, texte coupé dans sa cellule. */
  tableau(entetes: string[], lignes: string[][], poids?: number[]): void {
    const p = poids ?? entetes.map(() => 1);
    const total = p.reduce((a, b) => a + b, 0);
    const ws = p.map((x) => (x / total) * this.largeurUtile);
    const taille = 9;
    const rangee = (cells: string[], gras: boolean, c: Rgb): void => {
      const coupees = cells.map((cell, i) => this.couper(cell, taille, ws[i]! - 6, gras));
      const haut = Math.max(1, ...coupees.map((x) => x.length)) * taille * 1.3 + 5;
      this.place(haut);
      let x = this.marge;
      coupees.forEach((ls, i) => {
        ls.forEach((l, k) => this.ecrire(x, this.y - taille - 2 - k * taille * 1.3, l, taille, gras, c));
        x += ws[i]!;
      });
      this.y -= haut;
      this.flux += `0.88 0.88 0.88 RG 0.5 w ${this.marge} ${this.y.toFixed(2)} m ${(this.l - this.marge).toFixed(2)} ${this.y.toFixed(2)} l S\n`;
    };
    rangee(entetes, true, GRIS);
    for (const l of lignes) rangee(l, false, NOIR);
    this.y -= 6;
  }

  /** Une barre horizontale proportionnelle, avec son libellé et son nombre. */
  barre(libelle: string, n: number, max: number): void {
    const taille = 9.5;
    this.place(16);
    const yb = this.y - 12;
    const xLib = this.marge;
    const wLib = this.largeurUtile * 0.4;
    const lib = this.couper(libelle, taille, wLib - 8)[0] ?? "";
    this.ecrire(xLib, yb + 2, lib, taille, false, NOIR);
    const xb = this.marge + wLib;
    const wb = this.largeurUtile * 0.5;
    this.flux += `0.9 0.9 0.9 rg ${xb.toFixed(2)} ${yb.toFixed(2)} ${wb.toFixed(2)} 8 re f\n`;
    const k = max > 0 ? n / max : 0;
    if (k > 0) this.flux += `0.1 0.1 0.1 rg ${xb.toFixed(2)} ${yb.toFixed(2)} ${(wb * k).toFixed(2)} 8 re f\n`;
    this.ecrire(xb + wb + 8, yb + 1, String(n), taille, true, NOIR);
    this.y -= 16;
  }

  /** Le fichier, en octets. */
  octets(): Uint8Array {
    if (this.flux) this.pages.push(this.flux);
    this.flux = "";
    const n = this.pages.length;
    // Numéros d'objets : 1 catalogue, 2 pages, 3 F1, 4 F2, puis (page, contenu) par page.
    const objets: string[] = [];
    const kids = this.pages.map((_, i) => `${5 + i * 2} 0 R`).join(" ");
    objets[1] = "<< /Type /Catalog /Pages 2 0 R >>";
    objets[2] = `<< /Type /Pages /Kids [${kids}] /Count ${n} >>`;
    objets[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
    objets[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
    this.pages.forEach((flux, i) => {
      const pied = `BT 0.55 0.55 0.55 rg /F1 8 Tf 1 0 0 1 ${this.marge} 24 Tm ${litteral(this.pied)} Tj ET\nBT 0.55 0.55 0.55 rg /F1 8 Tf 1 0 0 1 ${(this.l - this.marge - 30).toFixed(2)} 24 Tm ${litteral(`${i + 1} / ${n}`)} Tj ET\n`;
      const contenu = flux + pied;
      objets[5 + i * 2] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${this.l} ${this.h}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${6 + i * 2} 0 R >>`;
      objets[6 + i * 2] = `<< /Length ${contenu.length} >>\nstream\n${contenu}endstream`;
    });
    let sortie = "%PDF-1.4\n%\xe2\xe3\xcf\xd3\n";
    const decalages: number[] = [];
    for (let i = 1; i < objets.length; i++) {
      decalages[i] = sortie.length;
      sortie += `${i} 0 obj\n${objets[i]}\nendobj\n`;
    }
    const xref = sortie.length;
    sortie += `xref\n0 ${objets.length}\n0000000000 65535 f \n`;
    for (let i = 1; i < objets.length; i++) sortie += `${String(decalages[i]).padStart(10, "0")} 00000 n \n`;
    sortie += `trailer\n<< /Size ${objets.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
    const b = new Uint8Array(sortie.length);
    for (let i = 0; i < sortie.length; i++) b[i] = sortie.charCodeAt(i) & 0xff;
    return b;
  }
}

/** Proposer un fichier au téléchargement. */
export function telecharger(nom: string, donnees: Uint8Array | string, type: string): void {
  const blob = new Blob([typeof donnees === "string" ? donnees : (donnees.buffer as ArrayBuffer)], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}
